import type { ChatEntity } from "@/indexedDB/types";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import { createLogger } from "@/utils/debug";

const log = createLogger("SyncAvatarSeed");

/**
 * Seeds UserAvatarMapState with P2P peer avatars from a FULL chat-list snapshot.
 *
 * WHY (GAP-2): Under VITE_USE_NEW_SYNC the sync engine writes each chat's
 * `avatar.attachmentId` to Dexie, but UserAvatarMapState is a SEPARATE, PERSISTED
 * store (localStorage) that `useUserAvatar` reads FIRST — a map entry (even an
 * empty one) OVERRIDES the fresh `chat.avatar.attachmentId`. So a peer who
 * changed their avatar while we were offline / between sessions keeps showing the
 * stale persisted map entry until a newer message or socket user-event re-seeds
 * it. The legacy sync path avoided this by seeding the map from the chat list as
 * ground truth (`chatsSync.ts` `seedAvatarMapFromChats`, priority=true). New-sync
 * never did. This mirrors the legacy behavior for the new-sync full snapshot.
 *
 * We write each P2P peer's avatar with `priority=true` so it wins over stale
 * non-priority (message-derived) entries via the store's `syncedAt` /
 * grace-period machinery (see UserAvatarMapState).
 *
 * SAFETY — seed NON-EMPTY attachmentIds ONLY:
 * The peer `avatar.attachmentId` is populated by a backend change (ow2-chat-be
 * PR #210, `p2p-avatar-sync`) that is NOT yet deployed. Before it ships the p2p
 * chat DTO carries `attachmentId: ""`. If we priority-tombstoned from empties we
 * would WIPE every peer avatar on every full snapshot — far worse than the bug we
 * are fixing. By skipping empties this is a harmless NO-OP until the BE ships,
 * then it activates automatically. (The store's `setUserAvatar` also rejects an
 * empty attachmentId, so this is defense-in-depth.)
 *
 * SCOPE: runs on BOTH the full-snapshot path (`paginateChatList`) and the
 * trusted-delta path (`upsertSyncedChats(mappedUpdatedChats)`). The bug's PRIMARY
 * scenario — a peer changed their avatar while you were OFFLINE — surfaces on
 * reconnect via a DELTA sync, not a full snapshot: `fetchEvents` carries no
 * USER_UPDATE event, so the peer's new avatar arrives ONLY through the delta's
 * `updatedChats[].avatar`, which we upsert but otherwise never propagate into the
 * map. To keep re-syncs cheap (delta `updatedChats` fire for ANY chat change,
 * e.g. a new message), the dedup below skips peers whose avatar is unchanged.
 *
 * RESIDUAL (documented, not fixed here): a priority write stamps a client `now`
 * timestamp; in a narrow window it could clobber a fresher socket-derived avatar
 * and grace-block repair for ~30s. This mirrors legacy `seedAvatarMapFromChats`;
 * the window is narrow and accepted.
 *
 * OUT OF SCOPE (residual): tombstoning a peer avatar the peer DELETED while we
 * were offline. Under new-sync the legacy empty->`removeUserAvatar` tombstone no
 * longer runs, so a deleted avatar shows stale until a socket event / new
 * message. Pre-#210 we cannot distinguish "deleted" from "not-populated" (both
 * `""`). Follow-up once #210 is live.
 *
 * Note: `avatar.secondMemberId` is, by construction, the OTHER member's username
 * (the mapper resolves it from the P2P `user_two`), so this never seeds the
 * current user; group chats have no `secondMemberId` and are skipped.
 */
export function seedAvatarMapFromSyncedChats(chats: ChatEntity[]): void {
  const { avatarMap, setUserAvatar } = useUserAvatarMapState.getState();
  const now = new Date().toISOString();
  let seeded = 0;

  for (const chat of chats) {
    const peer = chat.avatar?.secondMemberId;
    const attachmentId = chat.avatar?.attachmentId;
    // P2P peers only (group chats have no secondMemberId), and NON-EMPTY
    // attachmentIds only (empty = BE PR #210 not yet deployed — see SAFETY).
    if (!peer || !attachmentId) {
      continue;
    }
    // Skip the no-op write. `setUserAvatar`'s own dedup needs BOTH attachmentId
    // AND timestamp to match, but we pass a fresh `now` every call — so without
    // this guard EVERY unchanged peer would be re-written (O(N) map spread +
    // persist serialization + subscriber re-render) on every full-snapshot page
    // and every delta. Only write when the avatar actually changed.
    if (avatarMap[peer]?.attachmentId === attachmentId) {
      continue;
    }
    setUserAvatar(peer, attachmentId, now, true);
    seeded++;
  }

  if (seeded > 0) {
    log.info(
      `Seeded avatar map with ${seeded} P2P peer avatar(s) from snapshot`,
    );
  }
}
